import React, { useState } from 'react';
import { CheckCircle, XCircle, Eye, FileText, User } from 'lucide-react';
import { JobApplication, ApplicationStatusLabels } from '@/lib/models/jobApplication';
import { StatusBadge } from '@/components/custom-ui/status-badge';
import { IconChip } from '@/components/custom-ui/IconChip';
import { ActionButton, OutlineButton, DangerButton } from '@/components/custom-ui/action-button';
import { NEXILINK_ROW } from '@/lib/nexilink-card-styles';
import ApplicantProfileModal from './ApplicantProfileModal';

interface ApplicationCardProps {
    application: JobApplication;
    onStatusUpdate: (applicationId: string, status: 'reviewing' | 'accepted' | 'rejected', decisionNote?: string) => void;
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({ application, onStatusUpdate }) => {
    const isPending = application.status === 'pending';
    const isReviewing = application.status === 'reviewing';
    const canAct = isPending || isReviewing;
    const [showProfile, setShowProfile] = useState(false);

    return (
        <div className={`${NEXILINK_ROW} !items-start flex-col`}>
            <div className="flex items-center gap-3 w-full">
                <IconChip icon={FileText} />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                        {application.applicant_name || `Sökande #${application.user_id.slice(0, 8)}`}
                    </p>
                    <p className="text-xs text-gray-400">
                        Ansökt {new Date(application.applied_at).toLocaleDateString('sv-SE')}
                    </p>
                </div>
                <StatusBadge status={application.status} label={ApplicationStatusLabels[application.status]} />
            </div>

            {application.cover_letter && (
                <div className="mt-3 w-full p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{application.cover_letter}</p>
                </div>
            )}

            {/* "Visa profil" is always available so the employer can review the
                candidate (CV, contact, profile) before — and after — deciding.
                Decision buttons only while the application is actionable. */}
            <div className="flex flex-wrap gap-2 mt-3">
                <OutlineButton size="sm" icon={User} onClick={() => setShowProfile(true)}>
                    Visa profil
                </OutlineButton>
                {canAct && (
                    <>
                        {isPending && (
                            <OutlineButton size="sm" icon={Eye} onClick={() => onStatusUpdate(application.id, 'reviewing')}>
                                Granska
                            </OutlineButton>
                        )}
                        <ActionButton size="sm" icon={CheckCircle} onClick={() => onStatusUpdate(application.id, 'accepted')}>
                            Acceptera
                        </ActionButton>
                        <DangerButton size="sm" icon={XCircle} onClick={() => onStatusUpdate(application.id, 'rejected')}>
                            Avvisa
                        </DangerButton>
                    </>
                )}
            </div>

            {showProfile && (
                <ApplicantProfileModal
                    applicationId={application.id}
                    applicantName={application.applicant_name}
                    roleLabel={application.need_role || application.need_title}
                    coverLetter={application.cover_letter}
                    onClose={() => setShowProfile(false)}
                />
            )}
        </div>
    );
};

export default ApplicationCard;
